import { useRef, useState } from 'react'
import { DIETS, TIERS, GENDERS, WHO_WILL_PAY_OPTIONS, inputCls } from '../lib/constants'
import { API_BASE, storedToken } from '../lib/api'

export default function ProfileFields({ form, setForm }) {
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const fileInputRef = useRef(null)

  async function handleImageChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError('')
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('image', file)
      const token = storedToken()
      const res = await fetch(`${API_BASE}/auth/upload-avatar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      })
      const data = await res.json()
      if (!res.ok) { setUploadError(data.message || 'Upload failed.'); return }
      upd('profilePicture', data.url)
    } catch {
      setUploadError('Network error during upload.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Avatar picker */}
      <div className="flex items-center gap-4">
        <div
          className="h-20 w-20 rounded-2xl bg-orange-100 border-2 border-dashed border-orange-300 shrink-0 overflow-hidden flex items-center justify-center cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          {form.profilePicture
            ? <img src={form.profilePicture} alt="avatar" className="h-full w-full object-cover" />
            : <span className="text-3xl">📷</span>
          }
        </div>
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="rounded-xl border border-orange-300 bg-white px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50 disabled:opacity-50 transition"
          >
            {uploading ? 'Uploading…' : 'Upload Photo'}
          </button>
          <p className="mt-1 text-xs text-gray-400">JPEG, PNG, WebP · max 5 MB · required</p>
          {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleImageChange}
        />
      </div>

      <input placeholder="Address (optional)" value={form.address} onChange={e => upd('address', e.target.value)} className={inputCls} />
      <div className="grid grid-cols-2 gap-3">
        <input placeholder="City *" value={form.city} onChange={e => upd('city', e.target.value)} className={inputCls} />
        <input placeholder="Area / Neighbourhood *" value={form.area} onChange={e => upd('area', e.target.value)} className={inputCls} />
      </div>
      <select value={form.dietaryType} onChange={e => upd('dietaryType', e.target.value)} className={inputCls}>
        {DIETS.map(d => <option key={d}>{d}</option>)}
      </select>
      <select value={form.budgetVibe} onChange={e => upd('budgetVibe', e.target.value)} className={inputCls}>
        {TIERS.map(t => <option key={t}>{t}</option>)}
      </select>
      <select value={form.whoWillPay} onChange={e => upd('whoWillPay', e.target.value)} className={inputCls}>
        <option value="">Who will pay? *</option>
        {WHO_WILL_PAY_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
      </select>
      <input placeholder="Interests, e.g. Healthy, Street Food" value={form.interestsInput} onChange={e => upd('interestsInput', e.target.value)} className={inputCls} />
      <textarea placeholder="Short bio *" value={form.bio} onChange={e => upd('bio', e.target.value)} className={inputCls} rows={2} />
      <div className="grid grid-cols-2 gap-3">
        <select value={form.gender} onChange={e => upd('gender', e.target.value)} className={inputCls}>
          <option value="">Select gender *</option>
          {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          placeholder="Birth year *"
          value={form.birthYear}
          onChange={e => upd('birthYear', e.target.value.replace(/\D/g, '').slice(0, 4))}
          className={inputCls}
        />
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={form.isAvailable} onChange={e => upd('isAvailable', e.target.checked)} className="h-4 w-4 accent-orange-500" />
        <span className="text-sm font-semibold text-gray-700">Available for meals now</span>
      </label>
      {form.isAvailable && (
        <input placeholder="e.g. Looking for lunch today" value={form.availabilityLabel} onChange={e => upd('availabilityLabel', e.target.value)} className={inputCls} />
      )}
    </div>
  )
}
